import { LoginForm } from '@/components/auth/login-form'
import { Container } from '@/components/ui/container'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { LAYOUT_CONFIG } from '@/lib/config/layout'
import { cn } from '@/lib/utils'
import { usePopup } from '@/providers/popup-provider'
import Link from 'next/link'

export function TopBar({ isScrollingDown }: { isScrollingDown: boolean }) {
  const isLoggedIn = useIsLoggedIn()
  const { openPopup } = usePopup()

  const handleShowLoginPopup = (e: React.MouseEvent) => {
    e.preventDefault()
    openPopup(<LoginForm />)
  }

  return (
    <div
      className={cn(
        'h-8 transition-all duration-200',
        isScrollingDown && 'm-0 h-0 scale-y-95 overflow-hidden p-0'
      )}
      aria-hidden={isScrollingDown}
      data-tracking-level-1={'Topbar'}
    >
      <Container
        size='full'
        className='flex h-8 items-center justify-center bg-neutral-500 lg:justify-between'
      >
        <ul className='flex h-full items-center justify-evenly max-lg:flex-1 md:justify-between'>
          {LAYOUT_CONFIG.HEADER.TOP_BAR.LEFT.map((item, index, arr) => (
            <li
              key={index}
              className='relative flex h-full flex-1 items-center lg:flex-none'
            >
              <Link
                href={item.href}
                aria-label={item.title}
                className='block h-full flex-1 whitespace-nowrap px-2 py-2 pt-2.5 text-center font-criteria text-xs font-medium leading-3.5 text-neutral-200 transition-colors hover:bg-neutral-600'
                prefetch={false}
              >
                {item.title}
              </Link>
              <span
                className={cn(
                  'absolute right-0 z-10 my-2 block h-4.5 w-[1px] bg-neutral-300/20',
                  index === arr.length - 1 && 'hidden'
                )}
              ></span>
            </li>
          ))}
        </ul>

        <ul className='hidden h-full items-center justify-evenly max-lg:flex-1 md:justify-between lg:flex'>
          {LAYOUT_CONFIG.HEADER.TOP_BAR.RIGHT.map((item, index, arr) => {
            if (item.href === '/login' && isLoggedIn) {
              return null
            }

            // Check if next item will be hidden (login item when logged in)
            const nextItem = arr[index + 1]
            const isNextItemHidden = nextItem?.href === '/login' && isLoggedIn
            const isLastVisibleItem =
              index === arr.length - 1 || isNextItemHidden

            return (
              <li
                key={index}
                className='relative flex h-full flex-1 items-center lg:flex-none'
              >
                <Link
                  href={item?.href === '/login' ? '#' : item.href}
                  prefetch={false}
                  className='flex h-full flex-1 items-center gap-0.5 whitespace-nowrap px-2 py-2 pt-2.5 text-center font-criteria text-xs font-medium leading-3.5 text-neutral-200 transition-colors hover:bg-neutral-600'
                  onClick={(e) => {
                    if (item.href === '/login') {
                      e.preventDefault()
                      handleShowLoginPopup(e)
                    }
                  }}
                >
                  {item?.icon && (
                    <item.icon className='h-4 w-4 fill-neutral-200 stroke-neutral-200' />
                  )}
                  {item.title}
                </Link>
                <span
                  className={cn(
                    'absolute right-0 z-10 my-2 block h-4.5 w-[1px] bg-neutral-300/20',
                    isLastVisibleItem && 'hidden'
                  )}
                ></span>
              </li>
            )
          })}
        </ul>
      </Container>
    </div>
  )
}
